import { useState, useMemo, useRef, useEffect } from 'react';
import { eachDayOfInterval, format, parseISO } from 'date-fns';

export default function useDateRangeSelection({ selectedDate, stayNights, onRangeSelect }) {
  const dragRef = useRef(null);
  const [dragOrigin, setDragOrigin] = useState(null);
  const [clickActive, setClickActive] = useState(false);
  const [hoverDate, setHoverDate] = useState(null);

  const hoverAnchor = dragOrigin || selectedDate;
  const hoverBefore = hoverDate && hoverAnchor && hoverDate < hoverAnchor;

  const hoverDates = useMemo(() => {
    if (!hoverAnchor || (!dragOrigin && stayNights > 0) || !hoverDate || hoverDate === hoverAnchor) return new Set();
    const start = parseISO(hoverAnchor);
    const end = parseISO(hoverDate);
    const [from, to] = start < end ? [start, end] : [end, start];
    return new Set(eachDayOfInterval({ start: from, end: to }).map(d => format(d, 'yyyy-MM-dd')));
  }, [hoverAnchor, dragOrigin, stayNights, hoverDate]);

  const getHandlers = (dateString, isClickable) => {
    if (!onRangeSelect) return {};

    const handleMouseDown = isClickable
      ? e => {
          e.preventDefault();
          if (clickActive) {
            dragRef.current = null;
            if (dateString === selectedDate) {
              onRangeSelect(null, null);
            } else {
              const a = selectedDate;
              const b = dateString;
              onRangeSelect(a < b ? a : b, a < b ? b : a);
            }
            requestAnimationFrame(() => {
              setDragOrigin(null);
              setClickActive(false);
            });
            return;
          }
          dragRef.current = { origin: dateString, moved: false };
          setDragOrigin(dateString);
          onRangeSelect(dateString, null);
          setClickActive(true);
        }
      : undefined;

    const handleMouseUp = isClickable
      ? () => {
          if (dragRef.current?.moved) {
            const a = dragRef.current.origin;
            const b = dateString;
            onRangeSelect(a < b ? a : b, a < b ? b : a);
            dragRef.current = null;
            requestAnimationFrame(() => {
              setDragOrigin(null);
              setClickActive(false);
            });
          } else {
            dragRef.current = null;
            setDragOrigin(null);
          }
        }
      : undefined;

    const handleMouseEnter = isClickable
      ? () => {
          setHoverDate(dateString);
          if (dragRef.current && dragRef.current.origin !== dateString) {
            dragRef.current.moved = true;
          }
        }
      : undefined;

    const handleClick = isClickable
      ? () => {
          if (dragRef.current?.moved) return;
        }
      : undefined;

    return { handleMouseDown, handleMouseUp, handleMouseEnter, handleClick };
  };

  useEffect(() => {
    if (!dragOrigin) return;
    const handleDocumentMouseUp = () => {
      dragRef.current = null;
      setDragOrigin(null);
    };
    document.addEventListener('mouseup', handleDocumentMouseUp);
    return () => document.removeEventListener('mouseup', handleDocumentMouseUp);
  }, [dragOrigin]);

  return { hoverDate, hoverDates, hoverBefore, dragRef, getHandlers };
}
